import type {
	GenericFilterItem,
	ParametricFilter,
	SelectedFilter
} from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import {
	isArrayOptionChecked,
	isFlagOptionChecked,
	isParametricOptionChecked
} from "./isFilterOptionChecked";
import { formatValueText } from "./rangeSlider";

/** The option fields read here; Norce's option types differ per filter group. */
interface FilterOption {
	Id?: string | number | null;
	Name?: string | null;
	Code?: string | null;
	Value?: string | null;
	ParametricId?: number;
}

interface ActiveLabelText {
	/** Stock option label when Norce sends none ("I lager"). */
	inStock: string;
	/** Label of a yes/no parametric's only option ("Ja"). */
	yes: string;
	/** Store currency, placed around the price range like the slider does. */
	currency?: Parameters<typeof formatValueText>[1];
}

const isOptionChecked = (
	group: string,
	selected: SelectedFilter,
	option: FilterOption
): boolean => {
	if (group === "category")
		return isArrayOptionChecked(selected, option, "Category");
	if (group === "brands")
		return isArrayOptionChecked(selected, option, "Manufacturer");
	if (group === "flags")
		return isFlagOptionChecked(selected, option as GenericFilterItem);
	// Colour and every other group are parametrics.
	return isParametricOptionChecked(
		selected,
		option as unknown as ParametricFilter
	);
};

const optionLabel = (option: FilterOption, text: ActiveLabelText): string =>
	option.Id != null && isBooleanParametricValueId(option.Id)
		? text.yes
		: String(option.Name || option.Code || option.Value || "");

/**
 * The selected values of one filter group, as its toolbar dropdown shows them
 * after the title (row layout), e.g. ["Barr"] or ["150–300 SEK"].
 */
const getActiveFilterLabels = (
	group: string,
	options: unknown,
	selected: SelectedFilter | undefined,
	text: ActiveLabelText
): string[] => {
	if (!selected) return [];
	if (group === "price") {
		const range = selected[FilterTypes.PRICE];
		return Array.isArray(range) && range.length === 2
			? [formatValueText(`${range[0]}–${range[1]}`, text.currency)]
			: [];
	}
	const list = Array.isArray(options) ? (options as FilterOption[]) : [];
	if (group === "stock") {
		return selected[FilterTypes.ONHAND]?.length
			? [list[0]?.Name || text.inStock]
			: [];
	}
	return list
		.filter((option) => isOptionChecked(group, selected, option))
		.map((option) => optionLabel(option, text));
};

export { getActiveFilterLabels };
